// @flow

import React, { Fragment, PureComponent } from 'react';
import { Redirect, type Match } from 'react-router-dom';

import Download from 'components/fragments/Download';
import DropdownRadioInput from 'components/fragments/input/DropdownRadioInput';
import Loading from 'components/fragments/Loading';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import AnnuairesPageDetail from 'components/page/AnnuairesPageDetail';
import MemberPageDetail from 'components/page/MemberPageDetail';
import { DOCUMENTS_PER_PAGE } from 'constants/pageConstants';
import { SORT_NAME, SORT_DATE } from 'constants/sortConstants';
import JobDetailContainer from 'containers/job/JobDetailContainer';
import JobList from 'components/job/JobList';
import { STATUS_LOADING, STATUS_DEFAULT, STATUS_UNKNOWN, STATUS_FAILURE } from 'constants/statusConstants';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { BlocContent } from 'types/NewsletterBloc';
import type { DocumentType, MemberType, AnnuaireType, DepartmentType } from 'types/PageDetail';
import type { Status } from 'types/Status';
import { getMonthName } from 'utils/dateUtils';
import {
  sortDocumentsByName,
  sortDocumentByMonth,
  sortDocumentsByDate,
  getSeasonFromDocuments,
  getTypesFromDocuments
} from 'utils/documentsUtils';
import { tagsUtils } from 'utils/tagsUtils';

export type DispatchProps = {
  fetchPageDetail: (slug: string) => void
};

export type StateProps = {
  status: Status,
  title: string,
  content: Array<BlocContent>,
  documents: Array<DocumentType>,
  membersGroups: Array<{
    name: string,
    child: Array<MemberType>
  }>,
  annuaires: Array<AnnuaireType>,
  departmentsGroups: Array<DepartmentType>,
  meta_description: string,
  meta_title: string,
  template: string
};

type Props = {
  pageSlug: string,
  match: Match
} & DispatchProps &
  StateProps;

type State = {
  displayedDocuments: number,
  sort: SORT_NAME | SORT_DATE | { from: Date, to: Date },
  typeDocument: string
};

class PageDetail extends PureComponent<Props, State> {
  _pageContent: ?HTMLElement;
  _links: Array<any>;
  _tags: Object;
  props: Props;
  state: State = {
    displayedDocuments: DOCUMENTS_PER_PAGE,
    sort: SORT_DATE,
    typeDocument: ''
  };

  componentDidMount() {
    this.props.fetchPageDetail(this.props.pageSlug);
  }

  componentWillUnmount() {
    if (this._tags) {
      this._tags.removeTagsEvents();
    }
  }

  componentDidUpdate() {
    if (this._pageContent) {
      this._tags = tagsUtils(this._pageContent);
    }
  }

  handleSortChanged = (sort: SORT_NAME | { start: Date, end: Date }) => {
    this.setState({ sort });
  };

  handleTypeChanged = (typeDocument: string) => {
    this.setState({ typeDocument });
  };

  handleShowMoreDocuments = (e: MouseEvent) => {
    e.preventDefault();
    this.setState({ displayedDocuments: this.state.displayedDocuments + DOCUMENTS_PER_PAGE });
  };

  renderDetail = () => {
    const { content } = this.props;
    return content.map((blocFigure, index) => {
      return <ContentBloc key={index} {...blocFigure} />;
    });
  };

  render() {
    const { membersGroups, documents, departmentsGroups, annuaires, title, status, template } = this.props;

    if (status === STATUS_LOADING || status === STATUS_DEFAULT) {
      return <Loading isWhite />;
    }
    if (status === STATUS_UNKNOWN || status === STATUS_FAILURE) {
      return <Redirect to="/404" />;
    }

    switch (template) {
      case 'offre-emploi':
        return (
          <div className="page" ref={r => (this._pageContent = r)}>
            <JobDetailContainer />
          </div>
        );

      case 'page-emplois_liste_offre':
        return (
          <div className="page" ref={r => (this._pageContent = r)}>
            <JobList />
          </div>
        );

      default:
        return (
          <div className="page" ref={r => (this._pageContent = r)}>
            {this.renderDetail()}
            {documents.length > 0 && this.renderDocuments()}
            {membersGroups.length > 0 && <MemberPageDetail membersGroups={membersGroups} title={title} />}
            {departmentsGroups.length > 0 && <MemberPageDetail departmentsGroups={departmentsGroups} title={title} />}
            {annuaires.length > 0 && <AnnuairesPageDetail annuaires={annuaires} title={title} />}
          </div>
        );
    }
  }

  renderDocuments = () => {
    const { documents } = this.props;
    const { displayedDocuments, sort } = this.state;

    const options = getSeasonFromDocuments(documents).sort((a, b) => {
      return a.label > b.label ? -1 : a.label < b.label ? 1 : 0;
    });

    const documentTypes = getTypesFromDocuments(documents).sort((a, b) => {
      const x = a.label.toLowerCase();
      const y = b.label.toLowerCase();
      return x < y ? -1 : x > y ? 1 : 0;
    });

    let length = documents.length;

    if (sort !== SORT_NAME && sort !== SORT_DATE) {
      const documentVisibles = sortDocumentByMonth(documents, sort.from, sort.to, displayedDocuments);
      length = documentVisibles.length;
    }

    return (
      <React.Fragment>
        <div className="page__head mb-3 mt-4 mt-md-7">
          <h3>Dernières publications</h3>
          {documentTypes.length > 1 && (
            <DropdownRadioInput
              id="dropdown"
              maj={true}
              title={true}
              radio={true}
              marge={false}
              packed={true}
              options={[{ value: '', label: 'Tous les documents' }, ...documentTypes]}
              inputChanged={this.handleTypeChanged}
            />
          )}
          <DropdownRadioInput
            id="dropdown"
            maj={true}
            title={true}
            radio={true}
            marge={false}
            packed={true}
            options={[
              { value: SORT_DATE, label: 'Ordre antéchronologique' },
              { value: SORT_NAME, label: 'Ordre alphabétique' },
              ...options
            ]}
            inputChanged={this.handleSortChanged}
          />
        </div>
        {this.state.sort === SORT_NAME
          ? this.renderDocumentsByName()
          : this.state.sort === SORT_DATE
          ? this.renderDocumentsByDate()
          : this.renderDocumentsBySeason(this.state.sort.from, this.state.sort.to)}
        {length > DOCUMENTS_PER_PAGE && displayedDocuments < length && (
          <a
            role="button"
            tabIndex={0}
            className="link-chevron link-chevron--block link-chevron--center link-chevron--inline link-blur"
            data-label=""
            onClick={this.handleShowMoreDocuments}
          >
            <span>Afficher plus de publications</span>
            <i className="icon icon-zoom_plus" />
          </a>
        )}
      </React.Fragment>
    );
  };

  renderDocumentsByName = () => {
    return sortDocumentsByName(this.props.documents)
      .slice(0, this.state.displayedDocuments)
      .map((document, index) => (
        <Download
          key={`document_${document.id}`}
          title={document.title}
          date={document.date}
          label={'BULLETINS OFFICIELS'}
          src={document.src}
        />
      ));
  };

  renderDocumentsByDate = () => {
    const { documents } = this.props;
    const { typeDocument } = this.state;
    let documentsFiltered = documents;
    if (typeDocument) {
      documentsFiltered = documentsFiltered.filter(document => {
        return document.tags === typeDocument;
      });
    }
    const documentsToShow = sortDocumentsByDate(documentsFiltered);

    return documentsToShow
      .slice(0, this.state.displayedDocuments)
      .map((document, index) => (
        <Download
          key={`document_${document.id}`}
          title={document.title}
          date={document.date}
          label={document.tags}
          src={document.src}
        />
      ));
  };

  renderDocumentsBySeason = (from: Date, to: Date): any => {
    const documents = sortDocumentByMonth(this.props.documents, from, to, this.state.displayedDocuments);

    return documents.map((entry, index) => {
      const { documents, year, month } = entry;
      return (
        <Fragment key={`date${year}${month}`}>
          <h6>
            {getMonthName(month)} {year}
          </h6>
          {documents.map((document, index) => (
            <Download
              key={`document_${document.id}`}
              title={document.title}
              date={document.date}
              label={'BULLETINS OFFICIELS'}
              src={document.src}
            />
          ))}
        </Fragment>
      );
    });
  };
}

export default withHelmet(PageDetail)((Helmet, pageProps) => {
  return (
    <Helmet>
      <title>{pageProps.meta_title}</title>
      <meta name={'description'} content={pageProps.meta_description} />
      <link rel={'canonical'} href={window.location.href} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={pageProps.meta_title} />
      <meta property={'og:description'} content={pageProps.meta_description} />
    </Helmet>
  );
});
